import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { useBrand } from '../../context/BrandContext';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
  ToggleLeft,
  ToggleRight,
  Image,
  ExternalLink,
  Eye,
  MousePointerClick,
  Upload,
  ShoppingBag,
  Monitor
} from 'lucide-react';

function MonetizationTab() {
  const brand = useBrand();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settings, setSettings] = useState(null);
  const [promos, setPromos] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  // Promo editing state
  const [editingPromo, setEditingPromo] = useState(null); // null | 'new' | configId
  const [promoForm, setPromoForm] = useState({});
  const [savingPromo, setSavingPromo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    const response = await amplifyApi.getAdminMonetization();
    if (response.success) {
      setSettings(response.settings);
      setPromos(response.promos || []);
    } else {
      setError(response.error || 'Failed to load monetization config');
    }
    setLoading(false);
  };

  const handleToggleSetting = async (field) => {
    if (!settings) return;
    setSavingSettings(true);
    const newValue = !(settings[field] === true || settings[field] === 'true');
    const response = await amplifyApi.updateMonetizationSettings({
      ...settings,
      [field]: newValue
    });
    if (response.success) {
      setSettings(prev => ({ ...prev, [field]: newValue }));
      setSuccess(`${field} updated`);
    } else {
      setError(response.error || 'Failed to update');
    }
    setSavingSettings(false);
  };

  const handleUpdateSetting = async (field, value) => {
    if (!settings) return;
    setSavingSettings(true);
    const response = await amplifyApi.updateMonetizationSettings({
      ...settings,
      [field]: value
    });
    if (response.success) {
      setSettings(prev => ({ ...prev, [field]: value }));
      setSuccess('Setting updated');
    } else {
      setError(response.error || 'Failed to update');
    }
    setSavingSettings(false);
  };

  const startNewPromo = () => {
    setEditingPromo('new');
    setPromoForm({
      title: '',
      subtitle: '',
      imageUrl: '',
      imageS3Key: '',
      ctaText: 'Learn More',
      ctaUrl: '',
      isActive: true,
      displayOrder: promos.length + 1
    });
  };

  const startEditPromo = (promo) => {
    setEditingPromo(promo.configId);
    setPromoForm({
      title: promo.title || '',
      subtitle: promo.subtitle || '',
      imageUrl: promo.imageUrl || '',
      imageS3Key: promo.imageS3Key || '',
      ctaText: promo.ctaText || 'Learn More',
      ctaUrl: promo.ctaUrl || '',
      isActive: promo.isActive === 'true' || promo.isActive === true,
      displayOrder: parseInt(promo.displayOrder) || 0,
      startDate: promo.startDate || '',
      endDate: promo.endDate || ''
    });
  };

  const cancelEditPromo = () => {
    setEditingPromo(null);
    setPromoForm({});
  };

  const handlePromoImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const urlResponse = await amplifyApi.uploadMonetizationImage(file.type, ext);
      if (!urlResponse.success) {
        setError('Failed to get upload URL');
        setUploadingImage(false);
        return;
      }

      await fetch(urlResponse.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      setPromoForm(prev => ({
        ...prev,
        imageUrl: urlResponse.imageUrl,
        imageS3Key: urlResponse.s3Key
      }));
    } catch (err) {
      setError('Image upload failed');
    }
    setUploadingImage(false);
  };

  const handleSavePromo = async () => {
    if (!promoForm.title) {
      setError('Promo title is required');
      return;
    }
    setSavingPromo(true);
    setError(null);

    let response;
    if (editingPromo === 'new') {
      response = await amplifyApi.createMonetizationPromo(promoForm);
    } else {
      response = await amplifyApi.updateMonetizationPromo(editingPromo, promoForm);
    }

    if (response.success) {
      setSuccess(editingPromo === 'new' ? 'Promo created' : 'Promo updated');
      setEditingPromo(null);
      setPromoForm({});
      loadData();
    } else {
      setError(response.error || 'Failed to save promo');
    }
    setSavingPromo(false);
  };

  const handleDeletePromo = async (configId) => {
    if (!window.confirm('Delete this promo?')) return;
    const response = await amplifyApi.deleteMonetizationPromo(configId);
    if (response.success) {
      setSuccess('Promo deleted');
      loadData();
    } else {
      setError(response.error || 'Failed to delete promo');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const isEnabled = settings?.enabled === true || settings?.enabled === 'true';
  const isCrossSellEnabled = settings?.crossSellEnabled === true || settings?.crossSellEnabled === 'true';
  const isAdNetworkEnabled = settings?.adNetworkEnabled === true || settings?.adNetworkEnabled === 'true';

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* 1. Master Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Monetization</h2>
            <p className="text-sm text-slate-500 mt-1">
              Show ads, promos, and product recommendations during product creation
            </p>
          </div>
          <button
            onClick={() => handleToggleSetting('enabled')}
            disabled={savingSettings}
            className="flex items-center gap-2 text-sm font-medium"
          >
            {isEnabled ? (
              <ToggleRight className="w-8 h-8 text-green-500" />
            ) : (
              <ToggleLeft className="w-8 h-8 text-slate-400" />
            )}
            <span className={isEnabled ? 'text-green-600' : 'text-slate-500'}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
        </div>
      </div>

      {!isEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
          Monetization is disabled. Users will see the standard spinner during product creation.
        </div>
      )}

      {/* 2. Promo Cards */}
      <div className={`bg-white rounded-xl border border-slate-200 p-6 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Promo Cards</h3>
          </div>
          <button
            onClick={startNewPromo}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-medium transition-colors"
            style={{ backgroundColor: brand.colors.primary }}
          >
            <Plus className="w-4 h-4" /> Add Promo
          </button>
        </div>

        {/* Promo form (new or edit) */}
        {editingPromo && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={promoForm.title}
                  onChange={e => setPromoForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Summer Sale - 20% Off"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={promoForm.subtitle}
                  onChange={e => setPromoForm(prev => ({ ...prev, subtitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Use code SUMMER20 at checkout"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">CTA Text</label>
                <input
                  type="text"
                  value={promoForm.ctaText}
                  onChange={e => setPromoForm(prev => ({ ...prev, ctaText: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Shop Now"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">CTA URL</label>
                <input
                  type="url"
                  value={promoForm.ctaUrl}
                  onChange={e => setPromoForm(prev => ({ ...prev, ctaUrl: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/sale"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Image</label>
                <div className="flex items-center gap-2">
                  {promoForm.imageUrl && (
                    <img src={promoForm.imageUrl} alt="Preview" className="w-10 h-10 rounded object-cover" />
                  )}
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-50">
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingImage ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handlePromoImageUpload} className="hidden" />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Order</label>
                <input
                  type="number"
                  value={promoForm.displayOrder}
                  onChange={e => setPromoForm(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={promoForm.isActive}
                    onChange={e => setPromoForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600"
                  />
                  <span className="text-sm text-slate-700">Active</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleSavePromo}
                disabled={savingPromo}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors"
                style={{ backgroundColor: brand.colors.primary }}
              >
                {savingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingPromo === 'new' ? 'Create Promo' : 'Save Changes'}
              </button>
              <button
                onClick={cancelEditPromo}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Promos table */}
        {promos.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No promos yet. Click "Add Promo" to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-xs font-medium text-slate-500">Promo</th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500">Status</th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500">Order</th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500">
                    <Eye className="w-3.5 h-3.5 inline" /> Impr.
                  </th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500">
                    <MousePointerClick className="w-3.5 h-3.5 inline" /> Clicks
                  </th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500">CTR</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promos.map(promo => {
                  const impressions = parseInt(promo.impressionCount) || 0;
                  const clicks = parseInt(promo.clickCount) || 0;
                  const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(1) : '0.0';
                  const isActive = promo.isActive === 'true' || promo.isActive === true;

                  return (
                    <tr key={promo.configId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          {promo.imageUrl && (
                            <img src={promo.imageUrl} alt="" className="w-8 h-8 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-slate-800 truncate max-w-[200px]">{promo.title}</p>
                            {promo.ctaUrl && (
                              <a href={promo.ctaUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                                <ExternalLink className="w-3 h-3" /> Link
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center text-slate-600">{promo.displayOrder || 0}</td>
                      <td className="py-2 px-2 text-center text-slate-600">{impressions.toLocaleString()}</td>
                      <td className="py-2 px-2 text-center text-slate-600">{clicks.toLocaleString()}</td>
                      <td className="py-2 px-2 text-center text-slate-600">{ctr}%</td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => startEditPromo(promo)}
                            className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeletePromo(promo.configId)}
                            className="p-1.5 rounded hover:bg-red-100 text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 3. Ad Network */}
      <div className={`bg-white rounded-xl border border-slate-200 p-6 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Ad Network</h3>
          </div>
          <button
            onClick={() => handleToggleSetting('adNetworkEnabled')}
            disabled={savingSettings}
            className="flex items-center gap-2 text-sm"
          >
            {isAdNetworkEnabled ? (
              <ToggleRight className="w-6 h-6 text-green-500" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-slate-400" />
            )}
          </button>
        </div>

        {isAdNetworkEnabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
              <select
                value={settings?.adNetworkProvider || 'adsense'}
                onChange={e => handleUpdateSetting('adNetworkProvider', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="adsense">Google AdSense</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Publisher ID</label>
              <input
                type="text"
                value={settings?.adNetworkPublisherId || ''}
                onChange={e => handleUpdateSetting('adNetworkPublisherId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
                placeholder="ca-pub-XXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Ad Unit ID</label>
              <input
                type="text"
                value={settings?.adNetworkAdUnitId || ''}
                onChange={e => handleUpdateSetting('adNetworkAdUnitId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
                placeholder="YYYYYYYYYY"
              />
            </div>
            <p className="text-xs text-slate-400">
              Get your Publisher ID and Ad Unit ID from your Google AdSense dashboard. Ads will silently fail if users have ad blockers.
            </p>
          </div>
        )}
      </div>

      {/* 4. Cross-Sell */}
      <div className={`bg-white rounded-xl border border-slate-200 p-6 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Cross-Sell Products</h3>
          </div>
          <button
            onClick={() => handleToggleSetting('crossSellEnabled')}
            disabled={savingSettings}
            className="flex items-center gap-2 text-sm"
          >
            {isCrossSellEnabled ? (
              <ToggleRight className="w-6 h-6 text-green-500" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-slate-400" />
            )}
          </button>
        </div>

        {isCrossSellEnabled && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Number of products to show</label>
            <select
              value={settings?.crossSellCount || 3}
              onChange={e => handleUpdateSetting('crossSellCount', parseInt(e.target.value))}
              className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              {[1, 2, 3, 4, 5, 6].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-2">
              Shows random published products from the gallery during product creation. The current user's own products are excluded.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonetizationTab;
